import { useTranslation } from 'react-i18next';
import { Settings } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Breadcrumb, BreadcrumbItem } from '@/components/navigation/breadcrumb';

interface ProjectSettingsHeaderProps {
  /**
   * The project's name. Empty while it loads — the crumb and the subtitle then
   * say «Loading...». `null` — the name is not available to this viewer (no
   * permission to open the settings): no project crumb, no subtitle.
   */
  projectName: string | null;
}

/**
 * The header of project settings (#2078): «Aelo › Projects › <project> ›
 * Settings». The same one while the permissions and the settings load, when
 * access is refused and once the sections are on screen, so it does not pop in
 * after the load. The «Projects» crumb is the way back.
 */
export function ProjectSettingsHeader({ projectName }: ProjectSettingsHeaderProps) {
  const { t } = useTranslation(['projects', 'common']);
  const name =
    projectName === null ? null : projectName || t('common:common.loading', 'Loading...');

  return (
    <PageHeader
      data-testid="project-settings-header"
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
          <BreadcrumbItem href="/projects">
            {t('common:common.projects', 'Projects')}
          </BreadcrumbItem>
          {name !== null ? <BreadcrumbItem>{name}</BreadcrumbItem> : null}
          <BreadcrumbItem current>
            {t('common:common.settings', 'Settings')}
          </BreadcrumbItem>
        </Breadcrumb>
      }
      icon={Settings}
      title={t('projects.projectSettings', 'Project Settings')}
      subtitle={
        name !== null
          ? t('projects.projectSettingsSubtitle', 'Project: {{name}}', { name })
          : undefined
      }
    />
  );
}
